import React, { lazy, Suspense, useState } from 'react';
import { motion } from 'framer-motion';
import { FiArrowDown, FiDownload } from 'react-icons/fi';
import TypingAnimation from './TypingAnimation';
import SkillPills from './SkillPills';
import { ResumeData } from '../types/resume.types';

// Lazy load components
const PDFExport = lazy(() => import('./PDFExport'));
const ParticleBackground = lazy(() => import('./ParticleBackground'));

interface HeroSectionProps {
  data: ResumeData;
}

const HeroSection: React.FC<HeroSectionProps> = ({ data }) => {
  const [imageLoading, setImageLoading] = useState(true);
  
  const typingTexts = [
    'Automation Tester',
    'Python Practitioner',
    'CI/CD Crafter',
    'Senior QC Engineer'
  ];

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center pt-24 pb-16 section-padding overflow-hidden">
      {/* Enhanced gradient background — brand-tinted (Sprint 1 C2):
          warm cream → white → green-50 in light mode; IDE-dark gradient with a
          subtle amber tail in dark mode. */}
      <div className="absolute inset-0 bg-gradient-to-br from-surface-light via-white to-primary-50 dark:from-gray-900 dark:via-gray-900 dark:to-secondary-900/20"></div>
      
      {/* Subtle overlay pattern */}
      <div className="absolute inset-0 opacity-30 dark:opacity-20">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary-500/5 to-transparent"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-secondary-500/5 to-transparent"></div>
      </div>

      {/* Particle Background - lazy loaded */}
      <Suspense fallback={null}>
        <ParticleBackground particleCount={30} />
      </Suspense>

      <div className="container-width relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          
          {/* Profile Image */}
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ 
              type: "spring", 
              stiffness: 200, 
              damping: 20,
              delay: 0.2 
            }}
            className="mb-8 flex justify-center"
          >
            <motion.div 
              whileHover={{ 
                scale: 1.05, 
                rotate: 5,
                transition: { type: "spring", stiffness: 300, damping: 20 }
              }}
              className="relative w-40 h-40 md:w-48 md:h-48 lg:w-56 lg:h-56 rounded-full overflow-hidden ring-4 ring-white dark:ring-gray-800 shadow-2xl hover:shadow-3xl transition-all duration-300 cursor-pointer"
              // Brand green glow under the avatar (was rgba(59,130,246,...) blue).
              style={{ filter: 'drop-shadow(0 25px 50px rgba(61, 220, 132, 0.18))' }}
            >
              {/* Loading skeleton */}
              {imageLoading && (
                <div className="absolute inset-0 bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-800 animate-pulse"></div>
              )}
              
              <picture>
                <source srcSet="/avatar.webp" type="image/webp" />
                <img
                  src="/avatar.jpg"
                  alt={data.personalInfo.name}
                  className={`w-full h-full object-cover transition-opacity duration-500 ${imageLoading ? 'opacity-0' : 'opacity-100'}`}
                  loading="lazy"
                  width={500}
                  height={528}
                  onLoad={() => setImageLoading(false)}
                  onError={() => {
                    console.error('Failed to load avatar image');
                    setImageLoading(false);
                  }}
                />
              </picture>
              <div className="absolute inset-0 bg-gradient-to-br from-primary-500/10 to-secondary-500/10 hover:from-primary-500/20 hover:to-secondary-500/20 transition-all duration-300">
                <div className="absolute inset-0 bg-gradient-to-t from-black/5 via-transparent to-white/10"></div>
              </div>
            </motion.div>
          </motion.div>

          {/* Name */}
          <motion.h1
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-4xl md:text-6xl lg:text-7xl font-bold text-gray-900 dark:text-white mb-6"
            aria-label={`${data.personalInfo.name}, Senior Test Engineer`}
          >
            {data.personalInfo.name}
          </motion.h1>

          {/* Typing Animation */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mb-8"
          >
            <div className="text-2xl md:text-3xl lg:text-4xl font-semibold" aria-live="polite" aria-label="Current role">
              <TypingAnimation 
                texts={typingTexts}
                typingSpeed={80}
                deletingSpeed={40}
                pauseDuration={1500}
              />
            </div>
          </motion.div>

          {/* Brief description */}
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="text-base sm:text-lg md:text-xl text-gray-600 dark:text-gray-300 mb-8 sm:mb-12 max-w-2xl mx-auto leading-relaxed px-4 sm:px-0"
          >
            Experienced Senior Automation Test Engineer with 5+ years of expertise in building robust test automation frameworks and ensuring software quality.
          </motion.p>

          {/* Skill Pills */}
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 1.0 }}
            className="mb-8 sm:mb-12 px-4 sm:px-0"
          >
            <SkillPills skills={data.skills} maxItems={8} className="sm:max-w-4xl mx-auto" />
          </motion.div>

          {/* CTA Buttons */}
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 1.2 }}
            className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center px-4 sm:px-0 max-w-lg mx-auto"
          >
            {/* Primary CTA - Explore Skills */}
            <motion.button
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => scrollToSection('specialties')}
              className="group w-full sm:w-auto flex items-center justify-center space-x-2 bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold text-base sm:text-lg shadow-2xl hover:shadow-3xl transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
              // Primary CTA glow tinted to brand green (was rgba(59,130,246,...) blue).
              style={{
                filter: 'drop-shadow(0 10px 25px rgba(61, 220, 132, 0.35))',
                boxShadow: '0 0 30px rgba(61, 220, 132, 0.22), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
              }}
            >
              <span>Explore My Skills</span>
              <motion.div
                whileHover={{ x: 5 }}
                transition={{ duration: 0.2 }}
              >
                <FiArrowDown className="rotate-[-45deg]" size={18} />
              </motion.div>
            </motion.button>

            {/* Secondary CTA - Download Resume */}
            {/* Secondary CTA - re-skinned in Sprint 1 C2 from violet/purple to the
                brand amber ramp (secondary-* / accent-*) so it sits next to the
                green primary CTA as a deliberate green→amber pair. */}
            <Suspense fallback={
              <motion.button
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                className="w-full sm:w-auto flex items-center justify-center space-x-2 bg-gradient-to-r from-secondary-400 to-secondary-600 hover:from-secondary-500 hover:to-secondary-700 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold text-base sm:text-lg shadow-2xl hover:shadow-3xl transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
                style={{
                  filter: 'drop-shadow(0 10px 25px rgba(255, 184, 77, 0.35))',
                  boxShadow: '0 0 30px rgba(255, 184, 77, 0.22), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
                }}
              >
                <FiDownload size={20} />
                <span>Download Resume</span>
              </motion.button>
            }>
              <motion.div
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                className="w-full sm:w-auto bg-gradient-to-r from-secondary-400 to-secondary-600 hover:from-secondary-500 hover:to-secondary-700 text-white rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
                style={{
                  filter: 'drop-shadow(0 10px 25px rgba(255, 184, 77, 0.35))',
                  boxShadow: '0 0 30px rgba(255, 184, 77, 0.22), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
                }}
              >
                <PDFExport data={data} />
              </motion.div>
            </Suspense>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default HeroSection;